import React from 'react';
import {
  FileText, ClipboardList, Eye, Archive, BarChart3, Filter, ShieldAlert,
  Binoculars, ClipboardCheck, BookOpenCheck, ShieldCheck, Gavel, Rocket,
  Ticket, ArrowRight, AlertCircle, Plus, Activity,
} from 'lucide-react';
import { get } from '../api/http';
import { STAGE_SEQUENCE, STAGE_LABEL, stageUiId } from '../constants/demandStages';

/**
 * Home.
 *
 * Replaces a static page whose entire content was a 180px logo, a welcome banner
 * and two columns of gradient link cards — it told you nothing about the state of
 * the portfolio, so the first thing anyone did was click away from it.
 *
 * This one leads with live numbers, shows where work is stuck, and keeps navigation
 * as a compact secondary row. Typography follows the rest of the app (Merriweather
 * headings via the global h1-h6 rule, Source Sans Pro body).
 */

const CARD = 'rounded-2xl border border-gray-200 bg-white shadow-sm';

const STAGE_ICON = {
  TRIAGE: ShieldAlert, DISCOVERY: Binoculars, POC: ClipboardCheck,
  REQUIREMENTS: BookOpenCheck, SECURITY: ShieldCheck, TAB: Gavel, EXECUTION: Rocket,
};

function StatTile({ label, value, hint, tone = 'slate', icon: Icon, onClick }) {
  const tones = {
    slate: 'text-slate-700 bg-slate-50 ring-slate-200',
    indigo: 'text-indigo-700 bg-indigo-50 ring-indigo-200',
    amber: 'text-amber-700 bg-amber-50 ring-amber-200',
    emerald: 'text-emerald-700 bg-emerald-50 ring-emerald-200',
  };
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`${CARD} p-5 text-left transition hover:shadow-md disabled:hover:shadow-sm ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
          {/* Tabular numerals stop the row jittering as counts change on refresh. */}
          <div className="mt-2 text-3xl font-semibold tabular-nums text-gray-900">
            {value === null || value === undefined ? '—' : value}
          </div>
          {hint && <div className="mt-1 truncate text-xs text-gray-500">{hint}</div>}
        </div>
        {Icon && (
          <span className={`rounded-xl p-2 ring-1 ${tones[tone]}`}>
            <Icon size={18} />
          </span>
        )}
      </div>
    </button>
  );
}

export default function Home({ setActiveTab }) {
  const [dash, setDash] = React.useState(null);
  const [demandStats, setDemandStats] = React.useState(null);
  const [demands, setDemands] = React.useState([]);
  const [tickets, setTickets] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // These three are local database reads and return quickly.
        const [d, s, l] = await Promise.all([
          get('/dashboard/get_dashboard').catch(() => null),
          get('/demands/stats/summary').catch(() => null),
          get('/demands/list', { query: { limit: 200 } }).catch(() => null),
        ]);
        if (cancelled) return;
        setDash(d);
        setDemandStats(s);
        setDemands(l?.demands || []);
        if (!d && !s) setError('Could not reach the API.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Ticket count calls out to ServiceNow, which takes seconds. Kept out of the
  // block above so a slow or unreachable tenant never delays the whole page.
  React.useEffect(() => {
    let cancelled = false;
    get('/servicenow/tickets', { query: { limit: 200 } })
      .then((r) => { if (!cancelled) setTickets(r); })
      .catch(() => { if (!cancelled) setTickets({ count: null, unavailable: true }); });
    return () => { cancelled = true; };
  }, []);

  const countsByStage = React.useMemo(() => {
    const counts = Object.fromEntries(STAGE_SEQUENCE.map((s) => [s, 0]));
    demands.forEach((d) => {
      const ui = stageUiId(d.current_stage);
      const canonical = STAGE_SEQUENCE.find((s) => s.toLowerCase() === ui);
      if (canonical) counts[canonical] += 1;
    });
    return counts;
  }, [demands]);

  const awaitingTab = demands.filter((d) => stageUiId(d.current_stage) === 'tab').length;
  const readyToHandOff = demands.filter(
    (d) => stageUiId(d.current_stage) === 'execution' && !d.snow_project_id
  ).length;
  const recent = Object.entries(dash?.['Recent Activity'] || {});
  const maxStage = Math.max(1, ...Object.values(countsByStage));

  const go = (tab) => () => setActiveTab?.(tab);

  return (
    <div className="mx-auto max-w-7xl px-2 pb-10">
      {/* ---- Masthead ------------------------------------------------ */}
      <header className="pt-2 pb-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl text-gray-900">AI Enterprise Architect</h1>
            <p className="mt-1 text-gray-600">
              Ideas through screening, qualification and handoff to execution.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 font-medium text-emerald-700 ring-1 ring-emerald-200">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Database
            </span>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-medium ring-1 ${
              tickets?.unavailable
                ? 'bg-amber-50 text-amber-700 ring-amber-200'
                : 'bg-emerald-50 text-emerald-700 ring-emerald-200'}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${tickets?.unavailable ? 'bg-amber-500' : 'bg-emerald-500'}`} />
              ServiceNow
            </span>
          </div>
        </div>
      </header>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900">{error}</div>
      )}

      {/* ---- Numbers ------------------------------------------------- */}
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Ideas" value={dash?.['Total Ideas']} icon={FileText} tone="indigo"
                  hint={dash ? `${dash['Approved Ideas'] ?? 0} approved` : null}
                  onClick={go('ideas')} />
        <StatTile label="Pending reviews" value={dash?.['Pending Reviews']} icon={ClipboardList}
                  tone={dash?.['Pending Reviews'] ? 'amber' : 'slate'}
                  hint="Awaiting supervisor or ITPM" onClick={go('review')} />
        <StatTile label="Active demands" value={demandStats?.total_demands} icon={Filter} tone="indigo"
                  hint={demandStats ? `${demandStats.recent_7_days ?? 0} in last 7 days` : null}
                  onClick={go('demand-dashboard')} />
        <StatTile label="ServiceNow tickets"
                  value={tickets?.unavailable ? null : tickets?.count}
                  icon={Ticket} tone="emerald"
                  hint={tickets?.unavailable ? 'Tenant unreachable' : 'Created by AIEA'}
                  onClick={go('servicenow-tickets')} />
      </section>

      {/* ---- Pipeline ------------------------------------------------ */}
      <section className={`${CARD} mt-6 p-5`}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg text-gray-900">Demand pipeline</h2>
          <button onClick={go('demand-dashboard')}
                  className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:underline">
            Open dashboard <ArrowRight size={14} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {STAGE_SEQUENCE.map((stage) => {
            const Icon = STAGE_ICON[stage];
            const n = countsByStage[stage];
            return (
              <button key={stage} onClick={go(stage.toLowerCase())}
                      className="group rounded-xl border border-gray-200 p-3 text-left transition hover:border-indigo-300 hover:bg-indigo-50/40">
                <div className="flex items-center gap-2 text-gray-500 group-hover:text-indigo-600">
                  <Icon size={15} />
                  <span className="truncate text-xs font-medium">{STAGE_LABEL[stage]}</span>
                </div>
                <div className="mt-2 text-2xl font-semibold tabular-nums text-gray-900">{n}</div>
                {/* Bar is scaled to the busiest stage, so the shape of the
                    pipeline is readable at a glance rather than needing the numbers. */}
                <div className="mt-2 h-1 w-full rounded-full bg-gray-100">
                  <div className="h-1 rounded-full bg-indigo-500 transition-all"
                       style={{ width: `${(n / maxStage) * 100}%` }} />
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* ---- Needs attention --------------------------------------- */}
        <section className={`${CARD} p-5 lg:col-span-2`}>
          <h2 className="mb-4 text-lg text-gray-900">Needs attention</h2>
          <ul className="space-y-2">
            {[
              { n: dash?.['Pending Reviews'] ?? 0, label: 'ideas awaiting review', tab: 'review', icon: ClipboardList },
              { n: awaitingTab, label: 'demands awaiting a TAB decision', tab: 'tab', icon: Gavel },
              { n: readyToHandOff, label: 'approved demands not yet handed off to ServiceNow', tab: 'execution', icon: Rocket },
            ].filter((r) => r.n > 0).map((r) => (
              <li key={r.tab}>
                <button onClick={go(r.tab)}
                        className="flex w-full items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-left transition hover:bg-amber-100">
                  <r.icon size={18} className="shrink-0 text-amber-700" />
                  <span className="text-sm text-amber-900">
                    <strong className="tabular-nums">{r.n}</strong> {r.label}
                  </span>
                  <ArrowRight size={15} className="ml-auto shrink-0 text-amber-700" />
                </button>
              </li>
            ))}
            {/* An explicit all-clear reads better than an empty panel. */}
            {loading ? (
              <li className="py-6 text-center text-sm text-gray-500">Loading…</li>
            ) : (dash?.['Pending Reviews'] ?? 0) + awaitingTab + readyToHandOff === 0 && (
              <li className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                <AlertCircle size={16} /> Nothing is waiting on you right now.
              </li>
            )}
          </ul>

          <h3 className="mt-6 mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Quick actions
          </h3>
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'autoscribe', label: 'Submit an idea', icon: Plus },
              { id: 'approved-ideas', label: 'Promote to demand', icon: Filter },
              { id: 'sentinel', label: 'Idea Sentinel', icon: Eye },
              { id: 'backlogcurator', label: 'Backlog Curator', icon: Archive },
              { id: 'trm-portal', label: 'TRM Portal', icon: BarChart3 },
            ].map((a) => (
              <button key={a.id} onClick={go(a.id)}
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700">
                <a.icon size={15} /> {a.label}
              </button>
            ))}
          </div>
        </section>

        {/* ---- Recent activity --------------------------------------- */}
        <section className={`${CARD} p-5`}>
          <div className="mb-4 flex items-center gap-2">
            <Activity size={16} className="text-gray-400" />
            <h2 className="text-lg text-gray-900">Recent activity</h2>
          </div>
          {recent.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">
              {loading ? 'Loading…' : 'No ideas submitted today.'}
            </p>
          ) : (
            <ul className="space-y-3">
              {recent.map(([id, item]) => (
                <li key={id} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                  <div className="line-clamp-2 text-sm font-medium text-gray-900">
                    {item['Title & Sponsor'] || id}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                    <span className="font-mono">{id}</span>
                    {item['Submitted By'] && <span>· {item['Submitted By']}</span>}
                    <span className="ml-auto tabular-nums">{formatAgo(item['Ago in minutes'])}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

/** Minutes -> a human interval. The API returns total minutes, which reads badly
 *  past an hour or two. */
function formatAgo(minutes) {
  const m = Number(minutes);
  if (!Number.isFinite(m)) return '';
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
